import {
  ConditionalOperatorArray,
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  ValueFormatter,
  type FieldInformation,
  type FormFieldIsValidWhenCondition,
  type FormFieldIsVisibleWhenCondition,
  type FormFieldSchema,
  type InformationIsVisibleWhenCondition,
  type PrimitiveValue,
  type WatchedFormValues,
} from '@/types';
import DOMPurify from 'dompurify';
import { isNull, isNullOrUndefined, isUndefined } from './assertions';
import { formatDateInClaim } from './date';

interface SharedInformationParams {
  fieldValue: PrimitiveValue | PrimitiveValue[]; // Current field's value to compare.
}

interface SharedFormFieldParams {
  fieldName: string; // Current field's name. Only used for field-to-field comparisons.
  watchedFormValues: WatchedFormValues; // The update-to-date values of all form fields.
}

interface EvaluateConditionInformationParams extends SharedInformationParams {
  condition: InformationIsVisibleWhenCondition;
}

interface EvaluateConditionFormFieldParams extends SharedFormFieldParams {
  condition: FormFieldIsVisibleWhenCondition | FormFieldIsValidWhenCondition;
}

type EvaluateConditionParams =
  | EvaluateConditionFormFieldParams
  | EvaluateConditionInformationParams;

interface GetIsInformationVisibleParams extends SharedInformationParams {
  isVisibleWhen: FieldInformation['isVisibleWhen'];
}

interface GetIsFormFieldVisibleParams extends SharedFormFieldParams {
  isVisibleWhen: FormFieldSchema['isVisibleWhen'];
}

interface GetIsFormFieldValidParams extends SharedFormFieldParams {
  isValidWhen: FormFieldSchema['isValidWhen'];
}

interface FormatFieldValueParams {
  field: FormFieldSchema;
  value: FormFieldSchema['value'];
}

type FieldValueFormatter = (value: FormFieldSchema['value']) => string;

function isValuePrimitive(value: unknown): value is PrimitiveValue {
  return typeof value === 'string' || typeof value === 'number';
}

/**
 * Compares two primitive operands.
 * When both operands parse to finite numbers, they're compared numerically.
 * Otherwise they're compared as strings (including ISO date/time strings).
 * Returns a negative/zero/positive number, or 'null' if they can't be compared.
 */
function comparePrimitiveOperands(leftValue: PrimitiveValue, rightValue: PrimitiveValue) {
  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);
  const isLeftNumber = leftValue !== '' && Number.isFinite(leftNumber);
  const isRightNumber = rightValue !== '' && Number.isFinite(rightNumber);

  if (isLeftNumber && isRightNumber) {
    return leftNumber - rightNumber;
  }

  if (isLeftNumber !== isRightNumber) {
    return null;
  }

  return String(leftValue).localeCompare(String(rightValue));
}

function evaluateArrayCondition(
  operator: ConditionalOperatorArray,
  watchedFieldValue: FormFieldSchema['value'],
  valueFromCondition?: FormFieldIsVisibleWhenCondition['value'],
) {
  if (isUndefined(watchedFieldValue) || !Array.isArray(valueFromCondition)) {
    return false;
  }

  const watchedValueAsArray = !Array.isArray(watchedFieldValue)
    ? [watchedFieldValue]
    : watchedFieldValue;

  switch (operator) {
    case ConditionalOperatorArray.In:
      // Watched form values are 'in' values from condition.
      return watchedValueAsArray.every(value => valueFromCondition.includes(value));
    case ConditionalOperatorArray.NotIn:
      // Watched form values are 'not-in' values from condition.
      // NOTE: Can use '.some()', but keeping it simple to avoid confusion of logic.
      return !watchedValueAsArray.every(value => valueFromCondition.includes(value));
    case ConditionalOperatorArray.Includes:
      // Watched form values 'includes' all values from condition.
      return valueFromCondition.every(value => watchedValueAsArray.includes(value));
    case ConditionalOperatorArray.NotIncludes:
      // Watched form values 'not-includes' all values from condition.
      // NOTE: Can use '.some()', but keeping it simple to avoid confusion of logic.
      return !valueFromCondition.every(value => watchedValueAsArray.includes(value));
  }
}

function evaluatePrimitiveCondition(
  operator: ConditionalOperatorPrimitive,
  leftFieldValue: FormFieldSchema['value'],
  rightFieldValue: FormFieldSchema['value'],
) {
  if (!isValuePrimitive(leftFieldValue) || !isValuePrimitive(rightFieldValue)) {
    return false;
  }

  const comparison = comparePrimitiveOperands(leftFieldValue, rightFieldValue);

  switch (operator) {
    case ConditionalOperatorPrimitive.Equal:
      return comparison === 0;
    case ConditionalOperatorPrimitive.NotEqual:
      return comparison !== 0;
    /**
     * Relational operators require a valid comparison ('comparison' not null).
     * In JS 'null >= 0' is 'true', so the 'null' assetion/check is required.
     */
    case ConditionalOperatorPrimitive.GreaterThan:
      return !isNull(comparison) && comparison > 0;
    case ConditionalOperatorPrimitive.GreaterThanOrEqual:
      return !isNull(comparison) && comparison >= 0;
    case ConditionalOperatorPrimitive.LessThan:
      return !isNull(comparison) && comparison < 0;
    case ConditionalOperatorPrimitive.LessThanOrEqual:
      return !isNull(comparison) && comparison <= 0;
    default:
      return false;
  }
}

function assertParamsIsForInformation(
  params: EvaluateConditionParams,
): params is EvaluateConditionInformationParams {
  if ((params as EvaluateConditionInformationParams).fieldValue) {
    return true;
  }

  return false;
}

function assetParamsIsForFormField(
  params: EvaluateConditionParams,
): params is EvaluateConditionFormFieldParams {
  if ((params as EvaluateConditionFormFieldParams).fieldName) {
    return true;
  }

  return false;
}

/**
 * Evaluates a single visibility condition.
 * 'left' is the operand derived from either the current field's value
 * (field-to-field) or the watched 'field' value (when a 'value' is given).
 */
function evaluateCondition(params: EvaluateConditionParams) {
  if (assertParamsIsForInformation(params)) {
    const { fieldValue: currentFieldValue, condition } = params;
    const { value: valueFromCondition, operator } = condition;

    switch (operator) {
      case ConditionalOperatorArray.In:
      case ConditionalOperatorArray.NotIn:
      case ConditionalOperatorArray.Includes:
      case ConditionalOperatorArray.NotIncludes:
        return evaluateArrayCondition(operator, currentFieldValue, valueFromCondition);

      default: {
        return evaluatePrimitiveCondition(operator, currentFieldValue, valueFromCondition);
      }
    }
  }

  if (assetParamsIsForFormField(params)) {
    const { fieldName, condition, watchedFormValues } = params;
    const { field, value: valueFromCondition, operator } = condition;
    const watchedFieldValue = watchedFormValues[field];

    switch (operator) {
      case ConditionalOperatorArray.In:
      case ConditionalOperatorArray.NotIn:
      case ConditionalOperatorArray.Includes:
      case ConditionalOperatorArray.NotIncludes:
        return evaluateArrayCondition(operator, watchedFieldValue, valueFromCondition);

      default: {
        const isFieldToField = isUndefined(valueFromCondition);
        const leftFieldValue = isFieldToField ? watchedFormValues[fieldName] : watchedFieldValue;
        const rightFieldValue = isFieldToField ? watchedFieldValue : valueFromCondition;
        return evaluatePrimitiveCondition(operator, leftFieldValue, rightFieldValue);
      }
    }
  }

  return false;
}

/**
 * Determines whether field information should be visible or not, based on 'isVisibleWhen' conditions.
 * All conditions must pass (AND-ed together). Information with no conditions is considered as visible.
 */
function getIsInformationVisible({ fieldValue, isVisibleWhen }: GetIsInformationVisibleParams) {
  if (!isVisibleWhen?.length) {
    return true;
  }

  return isVisibleWhen.every(condition => evaluateCondition({ fieldValue, condition }));
}

/**
 * Determines whether a form field should be visible or not, based on 'isVisibleWhen' conditions.
 * All conditions must pass (AND-ed together). Field with no conditions is considered as visible.
 */
function getIsFormFieldVisible({
  fieldName,
  isVisibleWhen,
  watchedFormValues,
}: GetIsFormFieldVisibleParams) {
  if (!isVisibleWhen?.length) {
    return true;
  }

  return isVisibleWhen.every(condition =>
    evaluateCondition({ fieldName, condition, watchedFormValues }),
  );
}

/**
 * Determines whether a form field should be valid or not, based on 'isValidWhen' conditions.
 * All conditions must pass (AND-ed together). This is added on top of basic 'zod' validation.
 */
function getIsFormFieldValid({
  fieldName,
  isValidWhen,
  watchedFormValues,
}: GetIsFormFieldValidParams) {
  let message: string | undefined;

  /**
   * The field is assumed to be 'valid' if there are no 'isValidWhen' conditions passed.
   * Since this is added on top of 'zod', its basic field validations would still apply.
   */
  if (!isValidWhen?.length) {
    return { isValid: true, message };
  }

  const areAllConditionsValid = isValidWhen.every(condition => {
    const isThisConditionValid = evaluateCondition({ fieldName, condition, watchedFormValues });
    if (!isThisConditionValid) {
      message = condition.message;
    }

    return isThisConditionValid;
  });

  return { isValid: areAllConditionsValid, message };
}

function getCustomValueFormatter(valueFormatter: ValueFormatter): FieldValueFormatter {
  if (valueFormatter === ValueFormatter.Percentage) {
    return value => `${value}%`;
  }

  console.error(`no custom handler for ${valueFormatter}`);
  return value => `${value}`;
}

/**
 * Formats a form field's current value into its individual displayable/reportable
 * answer values (one entry per selected/entered value), for use in the claim
 * review step and the draft claim summary sent to the server for PDF generation.
 */
function formatFieldValue({ field, value }: FormatFieldValueParams): string[] {
  const { type, options, valueFormatter } = field;

  if (valueFormatter) {
    return [getCustomValueFormatter(valueFormatter)(value)];
  }

  if (isNullOrUndefined(value)) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];

  let formattedValues: PrimitiveValue[];
  switch (type) {
    case FormFieldSchemaType.Checkbox:
    case FormFieldSchemaType.Radio:
    case FormFieldSchemaType.Select: {
      formattedValues = values.map(
        currentValue =>
          options?.find(option => option.value === currentValue)?.label ?? `${currentValue}`,
      );
      break;
    }

    case FormFieldSchemaType.Date: {
      formattedValues = values.map(currentValue => formatDateInClaim(currentValue));
      break;
    }

    default: {
      formattedValues = values;
    }
  }

  return formattedValues.map(value => DOMPurify.sanitize(`${value}`));
}

export {
  evaluateArrayCondition,
  evaluatePrimitiveCondition,
  formatFieldValue,
  getIsFormFieldValid,
  getIsFormFieldVisible,
  getIsInformationVisible,
};
